import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { money, dateFmt, formatErr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Check, X, CheckSquare } from "lucide-react";

export default function Approvals() {
    const [rows, setRows] = useState(null);
    const [busy, setBusy] = useState("");

    const load = () => api.get("/approvals/pending").then((r) => setRows(r.data)).catch(() => setRows([]));
    useEffect(() => { load(); }, []);

    const act = async (id, kind) => {
        setBusy(id + kind);
        try {
            await api.post(`/purchase-requests/${id}/${kind}`, { comment: "" });
            toast.success(kind === "approve" ? "Approved" : "Rejected");
            await load();
        } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
        finally { setBusy(""); }
    };

    return (
        <div className="space-y-6 pe-fade-up">
            <div>
                <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-brand-emerald">Approval Inbox</h1>
                <p className="text-muted-foreground text-sm mt-1">Requests awaiting your approval.</p>
            </div>

            {rows === null ? (
                <div className="grid place-items-center py-16 text-brand-emerald"><Loader2 className="h-6 w-6 animate-spin" /></div>
            ) : rows.length === 0 ? (
                <div className="bg-white border border-border rounded-xl py-16 text-center text-muted-foreground">
                    <CheckSquare className="h-8 w-8 mx-auto mb-3 opacity-40" /> Nothing awaiting your approval. You're all caught up.
                </div>
            ) : (
                <div className="grid gap-4">
                    {rows.map((pr) => {
                        const step = pr.approval_chain.find((s) => s.status === "pending");
                        return (
                            <div key={pr.id} className="bg-white border border-border rounded-xl p-5" data-testid={`approval-card-${pr.id}`}>
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div>
                                        <Link to={`/app/requests/${pr.id}`} className="font-mono text-brand-teal hover:underline text-sm">{pr.pr_number}</Link>
                                        <div className="text-lg font-semibold text-brand-emerald mt-0.5">{money(pr.total)}</div>
                                        <div className="text-sm text-muted-foreground">{pr.vendor_name} · {pr.department || "—"} · by {pr.requester_name}</div>
                                        {step && <div className="text-xs text-amber-700 mt-1">Level {step.level}: {step.rule_name}</div>}
                                        {pr.justification && <div className="text-sm mt-2 text-foreground/80">"{pr.justification}"</div>}
                                    </div>
                                    <div className="flex gap-2">
                                        <Button disabled={busy} onClick={() => act(pr.id, "approve")} className="bg-emerald-600 hover:bg-emerald-700 rounded-lg" data-testid={`approve-${pr.id}`}>
                                            {busy === pr.id + "approve" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1" /> Approve</>}
                                        </Button>
                                        <Button disabled={busy} variant="outline" onClick={() => act(pr.id, "reject")} className="rounded-lg border-rose-200 text-rose-700 hover:bg-rose-50" data-testid={`reject-${pr.id}`}>
                                            <X className="h-4 w-4 mr-1" /> Reject
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
